import { ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";

type Size = "sm" | "md" | "lg";

function tier(score: number) {
  if (score >= 85) return { label: "Elite", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  if (score >= 70) return { label: "Trusted", tone: "bg-sky-50 text-sky-700 border-sky-200" };
  if (score >= 50) return { label: "Rising", tone: "bg-amber-50 text-amber-700 border-amber-200" };
  return { label: "New", tone: "bg-muted text-muted-foreground border-border" };
}

export function TrustScore({
  score,
  size = "md",
  showLabel = true,
  className,
}: {
  score: number | null | undefined;
  size?: Size;
  showLabel?: boolean;
  className?: string;
}) {
  const s = Math.max(0, Math.min(100, Math.round(Number(score ?? 0))));
  const t = tier(s);
  const padding = size === "sm" ? "px-2 py-0.5 text-[11px]" : size === "lg" ? "px-3 py-1.5 text-sm" : "px-2.5 py-1 text-xs";
  const iconSize = size === "sm" ? "h-3 w-3" : size === "lg" ? "h-4 w-4" : "h-3.5 w-3.5";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border font-semibold whitespace-nowrap",
        t.tone,
        padding,
        className,
      )}
      title={`Trust Score ${s}/100 · ${t.label}`}
      aria-label={`Trust score ${s} out of 100, ${t.label}`}
    >
      <ShieldCheck className={iconSize} aria-hidden />
      {s}
      {showLabel && <span className="opacity-80">· {t.label}</span>}
    </span>
  );
}

export function TrustScoreBreakdown({
  verificationPts,
  jobsPts,
  reviewPts,
  speedPts,
  reliabilityPts,
  repeatPts,
  total,
}: {
  verificationPts: number;
  jobsPts: number;
  reviewPts: number;
  speedPts: number;
  reliabilityPts: number;
  repeatPts: number;
  total: number;
}) {
  const rows = [
    { label: "Verification", value: verificationPts, max: 25 },
    { label: "Completed jobs", value: jobsPts, max: 20 },
    { label: "Review quality", value: reviewPts, max: 25 },
    { label: "Response speed", value: speedPts, max: 10 },
    { label: "Reliability", value: reliabilityPts, max: 10 },
    { label: "Repeat customers", value: repeatPts, max: 10 },
  ];
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <p className="text-sm font-semibold text-foreground">Trust score</p>
        <p className="font-display text-2xl font-bold text-foreground">{Math.round(total)}<span className="text-sm font-normal text-muted-foreground">/100</span></p>
      </div>
      <ul className="space-y-1.5">
        {rows.map((r) => (
          <li key={r.label} className="grid grid-cols-[auto,1fr,auto] items-center gap-3 text-xs">
            <span className="text-muted-foreground">{r.label}</span>
            <span className="h-1.5 overflow-hidden rounded-full bg-muted">
              <span className="block h-full rounded-full bg-primary" style={{ width: `${Math.min(100, (r.value / r.max) * 100)}%` }} />
            </span>
            <span className="tabular-nums font-medium text-foreground">{Math.round(r.value)}/{r.max}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Pure-JS mirror of public.compute_trust_score, for UI breakdown display only. */
export function computeTrustBreakdown(input: {
  id_verified: boolean;
  background_check_passed: boolean;
  kyc_reviewed_at?: string | null;
  jobs_completed: number;
  avg_rating: number;
  rating_count: number;
  response_time_minutes: number | null;
  completion_rate: number | null;
  repeat_booking_rate: number | null;
}) {
  let verificationPts = 0;
  if (input.id_verified) verificationPts += 10;
  if (input.background_check_passed) verificationPts += 10;
  if (input.kyc_reviewed_at) verificationPts += 5;

  const jobsPts = Math.min(input.jobs_completed / 50, 1) * 20;
  const confidence = Math.min((input.rating_count ?? 0) / 10, 1);
  const reviewPts = input.rating_count > 0 ? ((input.avg_rating ?? 0) / 5) * 25 * confidence : 0;

  let speedPts = 3;
  const rt = input.response_time_minutes;
  if (rt != null) {
    if (rt <= 15) speedPts = 10;
    else if (rt <= 60) speedPts = 7;
    else if (rt <= 240) speedPts = 4;
    else speedPts = 1;
  }

  const reliabilityPts = (input.completion_rate ?? 1) * 10;
  const repeatPts = Math.min(input.repeat_booking_rate ?? 0, 1) * 10;
  const total = Math.max(0, Math.min(100, Math.round(verificationPts + jobsPts + reviewPts + speedPts + reliabilityPts + repeatPts)));
  return { verificationPts, jobsPts, reviewPts, speedPts, reliabilityPts, repeatPts, total };
}
